import {
  Button,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { SubscriptionModel } from 'types'

export default function SubscriptionAssignPage() {
  const { login } = useParams<{ login: string }>()
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState<SubscriptionModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('token')
    axios
      .get(`${base_url}/subscription/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSubscriptions(res.data)
      })
      .catch((err) => {
        console.error('Failed to load subscriptions:', err)
        alert('Impossible de charger la liste des abonnements.')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleSubmit = () => {
    if (!selectedSubId) {
      alert('Veuillez sélectionner un abonnement.')
      return
    }

    setIsSaving(true)
    const token = localStorage.getItem('token')
    const payload = { subscriptionId: selectedSubId }

    // This single endpoint handles both assigning a new subscription (POST)
    // and changing an existing one (PUT). We'll use PUT as it can
    // gracefully handle both creation and update on some server setups,
    // or we can add logic to check if a sub already exists. For simplicity,
    // we will start with PUT.
    // A more robust solution might first check if the user has a subscription.
    axios
      .put(`${base_url}/user/${login}/subscription`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Abonnement mis à jour avec succès !')
        navigate(`/user/${login}`)
      })
      .catch((error) => {
        // If the user has no subscription, PUT might fail. Try POST.
        if (error.response && error.response.status === 400) {
          axios
            .post(`${base_url}/user/${login}/subscription`, payload, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
              alert('Abonnement assigné avec succès !')
              navigate(`/user/${login}`)
            })
            .catch((postError) => {
              console.error('Failed to assign subscription:', postError)
              alert(
                'La mise à jour a échoué: ' +
                  (postError.response?.data?.message || 'Erreur inconnue')
              )
            })
        } else {
          console.error('Failed to update subscription:', error)
          alert(
            'La mise à jour a échoué: ' +
              (error.response?.data?.message || 'Erreur inconnue')
          )
        }
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement des abonnements...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Gérer l'abonnement pour ${login}`}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Sélectionner un nouvel abonnement</Typography>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <List>
            {subscriptions.map((sub, index) => (
              <Box key={sub.id}>
                <ListItem
                  button
                  selected={selectedSubId === sub.id}
                  onClick={() => setSelectedSubId(sub.id!)}
                >
                  <ListItemText
                    primary={sub.name}
                    secondary={`Prix: ${sub.price}€ - Profils: ${sub.maxProfiles} - Licences: ${sub.maxLicenses}`}
                  />
                </ListItem>
                {index < subscriptions.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving || !selectedSubId}
          sx={{ mt: 2, minWidth: '200px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : "Confirmer l'abonnement"}
        </Button>
      </Box>
    </PageAppBar>
  )
}
